import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Header } from '../components/ui/header';
import { Footer } from '../components/ui/footer';
import { WhatsAppCTA } from '../components/ui/whatsapp-cta';
import { AppointmentForm } from '../components/ui/appointment-form';
import { Breadcrumb } from '../components/ui/Breadcrumb';
import { API_BASE } from '../config/api';
import { ensureAbsoluteUrl, truncateText } from '../utils/socialMeta';
import { renderTipTapToHTML } from '../utils/tiptapRenderer';

export const ProgramDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [program, setProgram] = useState(null);
  const [university, setUniversity] = useState(null);
  const [scholarships, setScholarships] = useState([]);
  const [relatedPrograms, setRelatedPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAppointmentForm, setShowAppointmentForm] = useState(false);
  const [expandedSection, setExpandedSection] = useState('');
  const [expandedModule, setExpandedModule] = useState(null);

  const backendUrl = API_BASE;

  useEffect(() => {
    const fetchProgramData = async () => {
      try {
        setLoading(true);
        
        // Fetch program details first by slug (needed for subsequent requests)
        const programResponse = await fetch(`${backendUrl}/api/public/programs/${slug}`);
        if (!programResponse.ok) {
          if (programResponse.status === 404) {
            throw new Error('Program not found');
          }
          throw new Error('Failed to fetch program details');
        }
        const programData = await programResponse.json();
        const program = programData.program;
        
        // Fetch university by ID (internal reference) - then fetch by slug for URL
        const universityId = program.university_id;
        
        // Guard against undefined university_id
        if (!universityId) {
          throw new Error('Program missing university_id');
        }
        
        // Get university data to find slug
        let universitySlug = null;
        try {
          const universityByIdResponse = await fetch(`${backendUrl}/api/public/universities/${universityId}`);
          if (universityByIdResponse.ok) {
            const universityByIdData = await universityByIdResponse.json();
            universitySlug = universityByIdData.university?.slug;
          }
        } catch (err) {
          console.warn('Failed to fetch university by ID:', err);
        }
        
        // Parallel fetch: university details (by slug if available, else ID), scholarships, and related data
        const fetchPromises = [
          universitySlug 
            ? fetch(`${backendUrl}/api/public/universities/${universitySlug}`)
            : fetch(`${backendUrl}/api/public/universities/${universityId}`),
          fetch(`${backendUrl}/api/public/scholarships?university_id=${universityId}`),
          fetch(`${backendUrl}/api/public/programs`),
          fetch(`${backendUrl}/api/public/universities`)
        ];
        
        const [universityResponse, scholarshipsResponse, allProgramsResponse, allUniversitiesResponse] = await Promise.all(fetchPromises);
        
        // Process university
        if (universityResponse.ok) {
          const universityData = await universityResponse.json();
          const fetchedUniversity = universityData.university;
          setUniversity(fetchedUniversity);
          setProgram({
            ...program,
            university_name: fetchedUniversity?.name || 'Unknown University'
          });
        } else {
          setProgram({
            ...program,
            university_name: 'Unknown University'
          });
        }
        
        // Process scholarships
        if (scholarshipsResponse.ok) {
          const scholarshipsData = await scholarshipsResponse.json();
          setScholarships(scholarshipsData.scholarships || []);
        }
        
        // Process related programs (requires all programs and universities)
        if (allProgramsResponse.ok && allUniversitiesResponse.ok) {
          const allProgramsData = await allProgramsResponse.json();
          const allUniversitiesData = await allUniversitiesResponse.json();
          
          const universityMap = {};
          (allUniversitiesData.universities || []).forEach(uni => {
            universityMap[uni.id] = uni.name;
          });
          
          // Filter related programs
          const related = (allProgramsData.programs || [])
            .filter(p => p.id !== program.id)
            .filter(p => 
              p.field_of_study_primary === program.field_of_study_primary ||
              p.level_of_study === program.level_of_study
            )
            .slice(0, 8)
            .map(p => ({
              ...p,
              university_name: universityMap[p.university_id] || 'Unknown University'
            }));
          
          setRelatedPrograms(related);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchProgramData();
    }
  }, [slug, backendUrl]);

  const handleConsultClick = () => {
    window.open('https://wa.me/60123456789?text=Hi, I would like to consult about this program', '_blank');
  };

  const handleAppointmentSubmit = async (formData) => {
    try {
      const response = await fetch(`${backendUrl}/api/forms/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          form_type: 'appointment_booking',
          ...formData,
          program_id: program?.id,
          program_name: program?.program_name
        })
      });
      
      if (response.ok) {
        setShowAppointmentForm(false);
        alert('Appointment request submitted successfully!');
      }
    } catch (err) {
      console.error('Failed to submit appointment:', err);
    }
  };

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? '' : section);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block w-12 h-12 border-4 border-[#F97316] border-t-transparent rounded-full animate-spin mb-4"></div>
          <p className="text-[#4B5563]">Loading program details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center px-6">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-[#111827] mb-4">Program Not Found</h1>
            <p className="text-[#4B5563] mb-6">{error}</p>
            <button 
              onClick={() => navigate('/programs')}
              className="px-6 py-3 bg-[#F97316] hover:bg-[#fb7e2e] text-white font-semibold rounded-lg transition-colors"
            >
              Browse All Programs
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!program) return null;

  // Prepare meta tags for program
  const progTitle = program.seo_title || `${program.program_name}${university?.name ? ` - ${university.name}` : ''}`;
  // Normalize level_of_study: prefer array, fallback to singular
  const levelText = (program.levels_of_study && Array.isArray(program.levels_of_study) && program.levels_of_study.length > 0)
    ? program.levels_of_study[0] 
    : (program.level_of_study || '');
  // Normalize mode_of_study: prefer array, fallback to singular
  const modeText = (program.modes_of_study && Array.isArray(program.modes_of_study) && program.modes_of_study.length > 0)
    ? program.modes_of_study[0]
    : (program.mode_of_study || '');
  const progDescription = truncateText(
    program.seo_description || program.short_description || 
    `${program.program_name}${university?.name ? ` at ${university.name}` : ''}. ${levelText ? levelText + ' program' : 'Program'}.`,
    200
  );
  const progImage = university?.logo || university?.images?.[0] || '';
  const progUrl = `https://www.unisave.my/programs/${program.slug || program.id}`;

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#FFFDFB' }}>
      {/* Open Graph & Twitter Card Meta for Program */}
      <Helmet>
        <title>{progTitle} | Unisave</title>
        <meta name="description" content={progDescription} />
        <link rel="canonical" href={progUrl} />
        
        {/* Open Graph Tags */}
        <meta property="og:title" content={progTitle} />
        <meta property="og:description" content={progDescription} />
        <meta property="og:image" content={ensureAbsoluteUrl(progImage)} />
        <meta property="og:url" content={progUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Unisave" />
        
        {/* Twitter Card Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={progTitle} />
        <meta name="twitter:description" content={progDescription} />
        <meta name="twitter:image" content={ensureAbsoluteUrl(progImage)} />
      </Helmet>
      
      <Header />
      
      <main className="flex-1 w-full max-w-full px-4 md:px-8 lg:px-16">
        {/* BREADCRUMB */}
        <div className="max-w-[1280px] mx-auto pt-4">
          <Breadcrumb items={[
            { label: 'Programs', href: '/programs' },
            { label: university?.display_name || university?.name || 'University', href: `/universities/${university?.slug || program.university_id}` },
            { label: program.program_name, href: '#' }
          ]} />
        </div>

        {/* SECTION 1: HERO - PROGRAM IDENTITY */}
        <section className="py-8 md:py-10 bg-white rounded-xl mt-6 border border-[#E5E7EB] shadow-sm">
          <div className="max-w-[1280px] mx-auto px-5 md:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Left: Program Info */}
              <div className="lg:col-span-2">
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-[#111827] mb-3 leading-tight" data-testid="program-name">
                  {program.program_name}
                </h1>
                <button
                  onClick={() => navigate(`/universities/${university?.slug || program.university_id}`)}
                  className="text-lg text-[#1E40AF] hover:text-[#1e40afdd] font-semibold mb-4 inline-flex items-center gap-2"
                  data-testid="university-link"
                >
                  {university?.logo && (
                    <img src={university.logo} alt={university.name} className="w-8 h-8 rounded-full object-cover" />
                  )}
                  {university?.name || 'University'}
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
                
                <div className="flex flex-wrap gap-2 mb-4">
                  <span className="px-3 py-1 bg-[#E0F2FE] text-[#0369A1] text-xs font-semibold rounded-md">
                    {levelText}
                  </span>
                  <span className="px-3 py-1 bg-[#F8FAFC] text-[#4B5563] text-xs font-semibold rounded-md border border-[#E5E7EB]">
                    {modeText}
                  </span>
                  <span className="px-3 py-1 bg-[#F8FAFC] text-[#4B5563] text-xs font-semibold rounded-md border border-[#E5E7EB]">
                    {program.duration}
                  </span>
                </div>
                
                {/* Short Description */}
                <div className="bg-gradient-to-r from-[#EFF6FF] to-[#DBEAFE] border-l-4 border-[#1E40AF] rounded-r-lg p-4 mb-4">
                  {program.short_description ? (
                    <p className="text-[#1E40AF] text-sm leading-relaxed font-medium">
                      {program.short_description}
                    </p>
                  ) : (
                    <p className="text-[#1E40AF] text-sm leading-relaxed font-medium">
                      This comprehensive program provides students with industry-relevant skills and knowledge, preparing them for successful careers in the field.
                    </p>
                  )}
                </div>
                
                {university?.location && (
                  <p className="text-[#6B7280] flex items-center gap-2 text-sm">
                    <svg className="w-4 h-4 text-[#F97316]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    {university.location}
                  </p>
                )}
              </div>
              
              {/* Right: Sticky CTA Card (Desktop) / Below on Mobile */}
              <div className="lg:col-span-1">
                <div className="bg-white border border-[#E5E7EB] rounded-xl p-5 lg:sticky lg:top-24 shadow-sm">
                  {program.cashback_amount > 0 && (
                    <div className="mb-4 relative overflow-hidden rounded-xl bg-gradient-to-br from-[#D1FAE5] to-[#A7F3D0] p-5">
                      <div className="absolute top-0 right-0 w-32 h-32 bg-white/20 rounded-full -mr-16 -mt-16"></div>
                      <div className="relative">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="w-8 h-8 bg-[#059669] rounded-full flex items-center justify-center">
                            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </div>
                          <p className="text-sm text-[#065F46] font-bold">Cashback Up To</p>
                        </div>
                        <p className="text-3xl font-bold text-[#065F46]">RM {program.cashback_amount.toLocaleString()}</p>
                      </div>
                    </div>
                  )}
                  
                  {program.cashback_amount > 0 && (
                    <div className="mb-4 relative overflow-hidden rounded-xl bg-gradient-to-br from-[#FEF3C7] via-[#FDE68A] to-[#FCD34D] p-4">
                      <div className="absolute top-0 right-0 w-24 h-24 bg-white/10 rounded-full -mr-12 -mt-12"></div>
                      <div className="relative flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 bg-gradient-to-br from-[#92400E] to-[#78350F] rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
                            <svg className="w-6 h-6 text-[#FEF3C7]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14zm-4 6v-7.5l4-2.222" />
                            </svg>
                          </div>
                          <div>
                            <p className="text-sm font-bold text-[#92400E]">Scholarship Available</p>
                            <p className="text-xs text-[#92400E] opacity-80">Financial aid opportunities</p>
                          </div>
                        </div>
                        <svg className="w-5 h-5 text-[#92400E]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </div>
                  )}
                  
                  <div className="space-y-3">
                    <button
                      onClick={handleConsultClick}
                      className="w-full px-6 py-3 bg-gradient-to-r from-[#F97316] to-[#fb7e2e] hover:shadow-xl text-white font-semibold rounded-xl transition-all"
                      data-testid="explore-advisor-button"
                    >
                      Explore with Advisor
                    </button>
                    <button
                      onClick={() => setShowAppointmentForm(true)}
                      className="w-full px-6 py-3 bg-white border-2 border-[#F97316] text-[#F97316] hover:bg-[#F97316] hover:text-white font-semibold rounded-xl transition-all"
                      data-testid="book-consultation-button"
                    >
                      Consult Us
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 2: QUICK FACTS BAR */}
        <section className="py-6 bg-white rounded-xl mt-5 border border-[#E5E7EB] shadow-sm">
          <div className="max-w-[1280px] mx-auto px-5 md:px-8">
            {/* Desktop: Horizontal Scrollable */}
            <div className="hidden md:flex gap-6 overflow-x-auto pb-2 scrollbar-hide">
              <div className="flex items-center gap-3 min-w-fit">
                <div className="w-12 h-12 bg-[#E0F2FE] rounded-full flex items-center justify-center flex-shrink-0">
                  <svg className="w-6 h-6 text-[#0369A1]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                </div>
                <div>
                  <p className="text-xs text-[#6B7280] font-medium">Level</p>
                  <p className="text-sm font-bold text-[#111827]">{levelText}</p>
                </div>
              </div>
              
              <div className="flex items-center gap-3 min-w-fit">
                <div className="w-12 h-12 bg-[#FEF3C7] rounded-full flex items-center justify-center flex-shrink-0">
                  <svg className="w-6 h-6 text-[#92400E]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <p className="text-xs text-[#6B7280] font-medium">Duration</p>
                  <p className="text-sm font-bold text-[#111827]">{program.duration}</p>
                </div>
              </div>
              
              <div className="flex items-center gap-3 min-w-fit">
                <div className="w-12 h-12 bg-[#F3E8FF] rounded-full flex items-center justify-center flex-shrink-0">
                  <svg className="w-6 h-6 text-[#7C3AED]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <p className="text-xs text-[#6B7280] font-medium">Mode</p>
                  <p className="text-sm font-bold text-[#111827]">{modeText}</p>
                </div>
              </div>
              
              {program.intake_months && program.intake_months.length > 0 && (
                <div className="flex items-center gap-3 min-w-fit">
                  <div className="w-12 h-12 bg-[#DBEAFE] rounded-full flex items-center justify-center flex-shrink-0">
                    <svg className="w-6 h-6 text-[#1E40AF]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-xs text-[#6B7280] font-medium">Intake</p>
                    <p className="text-sm font-bold text-[#111827]">{program.intake_months.join(', ')}</p>
                  </div>
                </div>
              )}
              
              {university?.location && (
                <div className="flex items-center gap-3 min-w-fit">
                  <div className="w-12 h-12 bg-[#FEE2E2] rounded-full flex items-center justify-center flex-shrink-0">
                    <svg className="w-6 h-6 text-[#DC2626]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-xs text-[#6B7280] font-medium">Location</p>
                    <p className="text-sm font-bold text-[#111827]">{university.location}</p>
                  </div>
                </div>
              )}
            </div>
            
            {/* Mobile: Grid Layout */}
            <div className="grid grid-cols-2 gap-4 md:hidden">
              <div className="flex items-center gap-3 bg-[#F8FAFC] rounded-lg p-3">
                <div className="w-10 h-10 bg-[#E0F2FE] rounded-full flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-[#0369A1]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                </div>
                <div>
                  <p className="text-xs text-[#6B7280] font-medium">Level</p>
                  <p className="text-xs font-bold text-[#111827] break-words">{levelText}</p>
                </div>
              </div>
              
              <div className="flex items-center gap-3 bg-[#F8FAFC] rounded-lg p-3">
                <div className="w-10 h-10 bg-[#FEF3C7] rounded-full flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-[#92400E]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <p className="text-xs text-[#6B7280] font-medium">Duration</p>
                  <p className="text-xs font-bold text-[#111827] break-words">{program.duration}</p>
                </div>
              </div>
              
              <div className="flex items-center gap-3 bg-[#F8FAFC] rounded-lg p-3">
                <div className="w-10 h-10 bg-[#F3E8FF] rounded-full flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-[#7C3AED]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <p className="text-xs text-[#6B7280] font-medium">Mode</p>
                  <p className="text-xs font-bold text-[#111827] break-words">{modeText}</p>
                </div>
              </div>
              
              {program.intake_months && program.intake_months.length > 0 && (
                <div className="flex items-center gap-3 bg-[#F8FAFC] rounded-lg p-3">
                  <div className="w-10 h-10 bg-[#DBEAFE] rounded-full flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 text-[#1E40AF]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-xs text-[#6B7280] font-medium">Intake</p>
                    <p className="text-xs font-bold text-[#111827] line-clamp-1 break-words">{program.intake_months.join(', ')}</p>
                  </div>
                </div>
              )}
              
              {university?.location && (
                <div className="flex items-center gap-3 bg-[#F8FAFC] rounded-lg p-3 col-span-2">
                  <div className="w-10 h-10 bg-[#FEE2E2] rounded-full flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 text-[#DC2626]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-xs text-[#6B7280] font-medium">Location</p>
                    <p className="text-xs font-bold text-[#111827] break-words">{university.location}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* SECTION 3: ABOUT THE PROGRAM */}
        <section className="py-8 bg-white rounded-xl mt-5 border border-[#E5E7EB] shadow-sm">
          <div className="max-w-[1280px] mx-auto px-5 md:px-8">
            <h2 className="text-xl md:text-2xl font-bold text-[#111827] mb-6">About the Program</h2>
            <div className="space-y-8">
              {/* Learning Outcomes */}
              {(program.learning_outcomes || (typeof program.learning_outcomes === 'string' && program.learning_outcomes.trim())) && (
                <div>
                  <h3 className="text-lg font-semibold text-[#111827] mb-4">Learning Outcomes</h3>
                  <div className="prose prose-lg max-w-none text-[#4B5563] leading-relaxed">
                    <div dangerouslySetInnerHTML={{ __html: program.learning_outcomes }} />
                  </div>
                </div>
              )}
              
              {/* Career Outcomes */}
              {(program.career_outcomes || (typeof program.career_outcomes === 'string' && program.career_outcomes.trim())) && (
                <div>
                  <h3 className="text-lg font-semibold text-[#111827] mb-4">Career Outcomes</h3>
                  <div className="prose prose-lg max-w-none text-[#4B5563] leading-relaxed">
                    <div dangerouslySetInnerHTML={{ __html: program.career_outcomes }} />
                  </div>
                </div>
              )}
              
              {/* Entry Pathways */}
              {program.entry_pathways && Array.isArray(program.entry_pathways) && program.entry_pathways.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-[#111827] mb-4">Entry Pathways</h3>
                  <ul className="list-disc pl-6 space-y-2 text-[#4B5563]">
                    {program.entry_pathways.map((pathway, index) => (
                      <li key={index}>{pathway}</li>
                    ))}
                  </ul>
                </div>
              )}
              
              {/* Fallback if no content */}
              {!program.learning_outcomes && !program.career_outcomes && (!program.entry_pathways || program.entry_pathways.length === 0) && (
                <p className="text-[#4B5563] leading-relaxed">
                  This program provides comprehensive education in {program.field_of_study_primary || 'the field'}, 
                  preparing students for successful careers with industry-relevant skills and knowledge.
                </p>
              )}
            </div>
          </div>
        </section>

        {/* SECTION 4: COURSE STRUCTURE / MODULES */}
        {(program.course_modules && program.course_modules.length > 0) || (program.program_modules && Object.keys(program.program_modules).length > 0) ? (
          <section className="py-8 bg-white rounded-xl mt-5 border border-[#E5E7EB] shadow-sm">
            <div className="max-w-[1280px] mx-auto px-5 md:px-8">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl md:text-2xl font-bold text-[#111827]">Course Structure</h2>
                <button
                  onClick={() => toggleSection('modules')}
                  className="md:hidden text-[#F97316] font-medium flex items-center gap-2"
                >
                  {expandedSection === 'modules' ? 'Collapse' : 'Expand'}
                  <svg className={`w-5 h-5 transition-transform ${expandedSection === 'modules' ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              </div>
              
              <div className={`space-y-6 ${expandedSection === 'modules' ? '' : 'hidden md:block'}`}>
                {/* Program Modules (TipTap content) */}
                {program.program_modules && Object.keys(program.program_modules).length > 0 && (
                  <div className="prose prose-lg max-w-none text-[#4B5563] leading-relaxed">
                    <div dangerouslySetInnerHTML={{ __html: renderTipTapToHTML(program.program_modules) }} />
                  </div>
                )}
                
                {/* Course Modules (structured list) */}
                {program.course_modules && program.course_modules.length > 0 && (
                  <div className="space-y-3">
                    {program.course_modules.map((module, index) => (
                      <div key={index} className="border-2 border-[#E5E7EB] rounded-lg">
                        <button
                          onClick={() => setExpandedModule(expandedModule === index ? null : index)}
                          className="w-full px-6 py-4 flex items-center justify-between hover:bg-[#F8FAFC] transition-colors"
                        >
                          <div className="text-left">
                            <h3 className="font-semibold text-[#111827]">{module.name || `Module ${index + 1}`}</h3>
                            {module.credits && <p className="text-sm text-[#6B7280]">{module.credits} credits</p>}
                          </div>
                          <svg className={`w-5 h-5 text-[#6B7280] transition-transform ${expandedModule === index ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                        {expandedModule === index && module.description && (
                          <div className="px-6 pb-4 text-[#4B5563]">
                            {module.description}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </section>
        ) : null}
        
        {/* SECTION 4.5: PROGRAM HIGHLIGHTS (Additional Content) */}
        {program.program_highlights && Object.keys(program.program_highlights).length > 0 && (
          <section className="py-8 bg-white rounded-xl mt-5 border border-[#E5E7EB] shadow-sm">
            <div className="max-w-[1280px] mx-auto px-5 md:px-8">
              <h2 className="text-xl md:text-2xl font-bold text-[#111827] mb-6">Program Highlights</h2>
              <div className="prose prose-lg max-w-none text-[#4B5563] leading-relaxed">
                <div dangerouslySetInnerHTML={{ __html: renderTipTapToHTML(program.program_highlights) }} />
              </div>
            </div>
          </section>
        )}

        {/* SECTION 5: FEES & COST */}
        <section className="py-8 bg-white rounded-xl mt-5 border border-[#E5E7EB] shadow-sm">
          <div className="max-w-[1280px] mx-auto px-5 md:px-8">
            <h2 className="text-xl md:text-2xl font-bold text-[#111827] mb-6">Fees & Cost</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              {/* Local Students */}
              <div className="border-2 border-[#E5E7EB] rounded-xl p-6">
                <h3 className="text-xl font-bold text-[#111827] mb-4 flex items-center gap-2">
                  <svg className="w-6 h-6 text-[#1E40AF]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                  </svg>
                  Local Students
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center pb-2 border-b border-[#E5E7EB]">
                    <span className="text-[#6B7280]">Total Estimated Cost</span>
                    <span className="text-xl font-bold text-[#111827]">
                      {program.estimated_fee_local 
                        ? `${program.fee_currency_local || 'RM'} ${program.estimated_fee_local.toLocaleString()}`
                        : 'N/A'}
                    </span>
                  </div>
                </div>
              </div>
              
              {/* International Students */}
              <div className="border-2 border-[#E5E7EB] rounded-xl p-6">
                <h3 className="text-xl font-bold text-[#111827] mb-4 flex items-center gap-2">
                  <svg className="w-6 h-6 text-[#F97316]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  International Students
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center pb-2 border-b border-[#E5E7EB]">
                    <span className="text-[#6B7280]">Total Estimated Cost</span>
                    <span className="text-xl font-bold text-[#111827]">
                      {program.estimated_fee_international 
                        ? `${program.fee_currency_international || 'RM'} ${program.estimated_fee_international.toLocaleString()}`
                        : 'N/A'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-[#FEF3C7] border-l-4 border-[#F97316] p-4 rounded-lg">
              <p className="text-sm text-[#92400E]">
                <span className="font-semibold">Note:</span> Fees shown are estimates and may vary by intake and year. 
                Please consult with our advisors for the most accurate information.
              </p>
            </div>
          </div>
        </section>

        {/* SECTION 6: ELIGIBILITY & ENTRY REQUIREMENTS */}
        {/* Only render if eligibility data exists */}
        {((Array.isArray(program.eligibility_requirements) && program.eligibility_requirements.length > 0) ||
          (program.english_requirements && typeof program.english_requirements === 'object' && 
           Object.values(program.english_requirements).some(v => v && v.trim && v.trim() !== ''))) && (
        <section className="py-8 bg-white rounded-xl mt-5 border border-[#E5E7EB] shadow-sm">
          <div className="max-w-[1280px] mx-auto px-5 md:px-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl md:text-2xl font-bold text-[#111827]">Eligibility & Entry Requirements</h2>
              <button
                onClick={() => toggleSection('eligibility')}
                className="md:hidden text-[#F97316] font-medium flex items-center gap-2"
              >
                {expandedSection === 'eligibility' ? 'Collapse' : 'Expand'}
                <svg className={`w-5 h-5 transition-transform ${expandedSection === 'eligibility' ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>
            
            <div className={`grid grid-cols-1 lg:grid-cols-3 gap-6 ${expandedSection === 'eligibility' || window.innerWidth >= 768 ? '' : 'hidden'}`}>
              {/* Left: Requirements Content (2 columns) */}
              <div className="lg:col-span-2 space-y-4">
                {/* Eligibility Requirements from Admin */}
                {Array.isArray(program.eligibility_requirements) && program.eligibility_requirements.map((req, index) => (
                  req.title || req.description ? (
                    <div key={index}>
                      {req.title && (
                        <h3 className="font-semibold text-[#111827] mb-3 flex items-center gap-2">
                          <svg className="w-5 h-5 text-[#F97316]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          {req.title}
                        </h3>
                      )}
                      {req.description && (
                        <p className="text-[#4B5563] pl-7 whitespace-pre-wrap">{req.description}</p>
                      )}
                    </div>
                  ) : null
                ))}
                
                {/* English Requirements from Admin */}
                {program.english_requirements && typeof program.english_requirements === 'object' && 
                 Object.values(program.english_requirements).some(v => v && v.trim && v.trim() !== '') && (
                  <div>
                    <h3 className="font-semibold text-[#111827] mb-3 flex items-center gap-2">
                      <svg className="w-5 h-5 text-[#F97316]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
                      </svg>
                      English Requirements
                    </h3>
                    <ul className="space-y-2 pl-7">
                      {program.english_requirements.ielts && <li className="text-[#4B5563]"><span className="font-medium">IELTS:</span> {program.english_requirements.ielts}</li>}
                      {program.english_requirements.toefl && <li className="text-[#4B5563]"><span className="font-medium">TOEFL:</span> {program.english_requirements.toefl}</li>}
                      {program.english_requirements.muet && <li className="text-[#4B5563]"><span className="font-medium">MUET:</span> {program.english_requirements.muet}</li>}
                      {program.english_requirements.other && <li className="text-[#4B5563]"><span className="font-medium">Other:</span> {program.english_requirements.other}</li>}
                    </ul>
                  </div>
                )}
                
                {/* CTA Button - opens consultation modal */}
                <button
                  onClick={handleConsultClick}
                  className="px-6 py-3 bg-white border-2 border-[#1E40AF] text-[#1E40AF] hover:bg-[#1E40AF] hover:text-white font-semibold rounded-lg transition-all"
                  data-testid="check-eligibility-button"
                >
                  Check Eligibility with Advisor
                </button>
              </div>
              
              {/* Right: Ad Banner (1 column) - only if ad_banners exist */}
              <div className="lg:col-span-1">
                {Array.isArray(program.ad_banners) && program.ad_banners.length > 0 && program.ad_banners[0].image_url ? (
                  <a 
                    href={program.ad_banners[0].target_url || '#'} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="block rounded-xl overflow-hidden border border-[#E5E7EB] hover:shadow-lg transition-shadow"
                  >
                    <img 
                      src={program.ad_banners[0].image_url} 
                      alt="Advertisement" 
                      className="w-full h-auto object-cover"
                    />
                  </a>
                ) : null}
              </div>
            </div>
          </div>
        </section>
        )}

        {/* SECTION 7: SCHOLARSHIPS APPLICABLE */}
        {scholarships.length > 0 && (
          <section className="py-8 bg-white rounded-xl mt-5 border border-[#E5E7EB] shadow-sm">
            <div className="max-w-[1280px] mx-auto px-5 md:px-8">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl md:text-2xl font-bold text-[#111827]">Scholarships Applicable</h2>
                <button
                  onClick={() => toggleSection('scholarships')}
                  className="md:hidden text-[#F97316] font-medium flex items-center gap-2"
                >
                  {expandedSection === 'scholarships' ? 'Collapse' : 'Expand'}
                  <svg className={`w-5 h-5 transition-transform ${expandedSection === 'scholarships' ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              </div>
              
              <div className={`${expandedSection === 'scholarships' || window.innerWidth >= 768 ? '' : 'hidden'}`}>
                {/* Desktop: Grid Layout */}
                <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
                  {scholarships.slice(0, 6).map((scholarship) => (
                    <div key={scholarship.id} className="border-2 border-[#E5E7EB] rounded-xl p-6 hover:border-[#F97316] hover:shadow-lg transition-all">
                      <h3 className="font-bold text-[#111827] mb-2">{scholarship.name}</h3>
                      <p className="text-sm text-[#6B7280] mb-3">{scholarship.provider || 'University'}</p>
                      <p className="text-lg font-bold text-[#F97316] mb-4">
                        {scholarship.amount ? `RM ${scholarship.amount.toLocaleString()}` : 'Value varies'}
                      </p>
                      <button 
                        onClick={() => navigate(`/scholarships/${scholarship.slug || scholarship.id}`)}
                        className="text-sm text-[#1E40AF] hover:text-[#1e40afdd] font-semibold flex items-center gap-1"
                      >
                        Learn More
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
                
                {/* Mobile: Horizontal Scrollable Carousel */}
                <div className="md:hidden relative">
                  <div className="flex gap-4 overflow-x-auto pb-4 mb-6 scrollbar-hide snap-x snap-mandatory" id="scholarships-carousel">
                    {scholarships.slice(0, 6).map((scholarship) => (
                      <div key={scholarship.id} className="border-2 border-[#E5E7EB] rounded-xl p-4 hover:border-[#F97316] transition-all min-w-[280px] flex-shrink-0 snap-center">
                        <h3 className="font-bold text-[#111827] mb-2 text-sm">{scholarship.name}</h3>
                        <p className="text-xs text-[#6B7280] mb-3">{scholarship.provider || 'University'}</p>
                        <p className="text-base font-bold text-[#F97316] mb-4">
                          {scholarship.amount ? `RM ${scholarship.amount.toLocaleString()}` : 'Value varies'}
                        </p>
                        <button 
                          onClick={() => navigate(`/scholarships/${scholarship.slug || scholarship.id}`)}
                          className="text-xs text-[#1E40AF] hover:text-[#1e40afdd] font-semibold flex items-center gap-1"
                        >
                          Learn More
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                  
                  {/* Navigation Arrows */}
                  <button 
                    onClick={() => document.getElementById('scholarships-carousel').scrollBy({left: -300, behavior: 'smooth'})}
                    className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-white/90 hover:bg-white border border-[#E5E7EB] rounded-full flex items-center justify-center shadow-lg z-10"
                  >
                    <svg className="w-4 h-4 text-[#6B7280]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <button 
                    onClick={() => document.getElementById('scholarships-carousel').scrollBy({left: 300, behavior: 'smooth'})}
                    className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-white/90 hover:bg-white border border-[#E5E7EB] rounded-full flex items-center justify-center shadow-lg z-10"
                  >
                    <svg className="w-4 h-4 text-[#6B7280]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
                
                <div className="text-center">
                  <button className="px-6 py-3 bg-[#F8FAFC] border-2 border-[#E5E7EB] text-[#111827] hover:border-[#1E40AF] hover:text-[#1E40AF] font-semibold rounded-lg transition-all">
                    View All Scholarships
                  </button>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* SECTION 8: RELATED / SIMILAR PROGRAMS */}
        {relatedPrograms.length > 0 && (
          <section className="py-8 bg-white rounded-xl mt-5 border border-[#E5E7EB] shadow-sm">
            <div className="max-w-[1280px] mx-auto px-5 md:px-8">
              <h2 className="text-xl md:text-2xl font-bold text-[#111827] mb-6">Related Programs</h2>
              
              {/* Desktop: Grid Layout */}
              <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                {relatedPrograms.slice(0, 4).map((relatedProgram) => (
                  <div
                    key={relatedProgram.id}
                    onClick={() => navigate(`/programs/${relatedProgram.slug || relatedProgram.id}`)}
                    className="bg-white border-2 border-[#E5E7EB] rounded-xl p-2.5 hover:border-[#F97316] hover:shadow-lg transition-all cursor-pointer flex flex-col h-full"
                  >
                    <h3 className="text-[#111827] mb-0.5 line-clamp-2 hover:text-[#F97316] transition-colors leading-tight" style={{ fontSize: '1rem', fontWeight: 650, minHeight: '2.5rem' }}>
                      {relatedProgram.program_name}
                    </h3>
                    <p className="text-[#4B5563] mb-1.5 font-semibold" style={{ fontSize: '0.80rem' }}>{relatedProgram.university_name}</p>
                    
                    <div className="flex flex-wrap gap-1 mb-2">
                      <span className="px-1.5 py-0.5 bg-[#E0F2FE] text-[#0369A1] font-semibold rounded" style={{ fontSize: '0.65rem' }}>
                        {(relatedProgram.levels_of_study && Array.isArray(relatedProgram.levels_of_study) && relatedProgram.levels_of_study.length > 0)
                          ? relatedProgram.levels_of_study[0]
                          : (relatedProgram.level_of_study || '')}
                      </span>
                      <span className="px-1.5 py-0.5 bg-[#F8FAFC] text-[#4B5563] rounded border border-[#E5E7EB]" style={{ fontSize: '0.65rem' }}>
                        {(relatedProgram.modes_of_study && Array.isArray(relatedProgram.modes_of_study) && relatedProgram.modes_of_study.length > 0)
                          ? relatedProgram.modes_of_study[0]
                          : (relatedProgram.mode_of_study || '')}
                      </span>
                    </div>
                    
                    <div className="space-y-1.5 mb-2">
                      {relatedProgram.estimated_fee_local && (
                        <div className="pt-1.5 border-t border-[#E5E7EB]">
                          <p className="text-[#6B7280] mb-0.5" style={{ fontSize: '0.65rem' }}>Local Student Fee</p>
                          <p className="font-bold text-[#111827]" style={{ fontSize: '0.8rem' }}>
                            RM {relatedProgram.estimated_fee_local.toLocaleString()}
                          </p>
                        </div>
                      )}
                      
                      {relatedProgram.estimated_fee_international && (
                        <div className="pt-1">
                          <p className="text-[#6B7280] mb-0.5" style={{ fontSize: '0.65rem' }}>International Student Fee</p>
                          <p className="font-bold text-[#111827]" style={{ fontSize: '0.8rem' }}>
                            RM {relatedProgram.estimated_fee_international.toLocaleString()}
                          </p>
                        </div>
                      )}
                    </div>
                    
                    <button
                      className="w-full mt-auto px-2.5 py-1.5 bg-gradient-to-r from-[#F97316] to-[#fb7e2e] hover:shadow-lg text-white font-semibold rounded-lg transition-all"
                      style={{ fontSize: '0.7rem' }}
                    >
                      Explore Program →
                    </button>
                  </div>
                ))}
              </div>
              
              {/* Mobile: Horizontal Scrollable Carousel */}
              <div className="md:hidden relative">
                <div className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide snap-x snap-mandatory" id="related-programs-carousel">
                  {relatedPrograms.slice(0, 8).map((relatedProgram) => (
                    <div
                      key={relatedProgram.id}
                      onClick={() => navigate(`/programs/${relatedProgram.slug || relatedProgram.id}`)}
                      className="bg-white border-2 border-[#E5E7EB] rounded-xl p-2.5 hover:border-[#F97316] hover:shadow-lg transition-all cursor-pointer flex flex-col min-w-[280px] flex-shrink-0 snap-center"
                    >
                      <h3 className="text-[#111827] mb-0.5 line-clamp-2 hover:text-[#F97316] transition-colors leading-tight" style={{ fontSize: '1rem', fontWeight: 650, minHeight: '2.5rem' }}>
                        {relatedProgram.program_name}
                      </h3>
                      <p className="text-[#4B5563] mb-1.5 font-semibold" style={{ fontSize: '0.80rem' }}>{relatedProgram.university_name}</p>
                      
                      <div className="flex flex-wrap gap-1 mb-2">
                        <span className="px-1.5 py-0.5 bg-[#E0F2FE] text-[#0369A1] font-semibold rounded" style={{ fontSize: '0.65rem' }}>
                          {(relatedProgram.levels_of_study && Array.isArray(relatedProgram.levels_of_study) && relatedProgram.levels_of_study.length > 0)
                            ? relatedProgram.levels_of_study[0]
                            : (relatedProgram.level_of_study || '')}
                        </span>
                        <span className="px-1.5 py-0.5 bg-[#F8FAFC] text-[#4B5563] rounded border border-[#E5E7EB]" style={{ fontSize: '0.65rem' }}>
                          {(relatedProgram.modes_of_study && Array.isArray(relatedProgram.modes_of_study) && relatedProgram.modes_of_study.length > 0)
                            ? relatedProgram.modes_of_study[0]
                            : (relatedProgram.mode_of_study || '')}
                        </span>
                      </div>
                      
                      <div className="space-y-1.5 mb-2">
                        {relatedProgram.estimated_fee_local && (
                          <div className="pt-1.5 border-t border-[#E5E7EB]">
                            <p className="text-[#6B7280] mb-0.5" style={{ fontSize: '0.65rem' }}>Local Student Fee</p>
                            <p className="font-bold text-[#111827]" style={{ fontSize: '0.8rem' }}>
                              RM {relatedProgram.estimated_fee_local.toLocaleString()}
                            </p>
                          </div>
                        )}
                        
                        {relatedProgram.estimated_fee_international && (
                          <div className="pt-1">
                            <p className="text-[#6B7280] mb-0.5" style={{ fontSize: '0.65rem' }}>International Student Fee</p>
                            <p className="font-bold text-[#111827]" style={{ fontSize: '0.8rem' }}>
                              RM {relatedProgram.estimated_fee_international.toLocaleString()}
                            </p>
                          </div>
                        )}
                      </div>
                      
                      <button
                        className="w-full mt-auto px-2.5 py-1.5 bg-gradient-to-r from-[#F97316] to-[#fb7e2e] hover:shadow-lg text-white font-semibold rounded-lg transition-all"
                        style={{ fontSize: '0.7rem' }}
                      >
                        Explore Program →
                      </button>
                    </div>
                  ))}
                </div>
                
                {/* Navigation Arrows */}
                <button 
                  onClick={() => document.getElementById('related-programs-carousel').scrollBy({left: -300, behavior: 'smooth'})}
                  className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-white/90 hover:bg-white border border-[#E5E7EB] rounded-full flex items-center justify-center shadow-lg z-10"
                >
                  <svg className="w-4 h-4 text-[#6B7280]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <button 
                  onClick={() => document.getElementById('related-programs-carousel').scrollBy({left: 300, behavior: 'smooth'})}
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-white/90 hover:bg-white border border-[#E5E7EB] rounded-full flex items-center justify-center shadow-lg z-10"
                >
                  <svg className="w-4 h-4 text-[#6B7280]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>
          </section>
        )}

        {/* SECTION 9: FINAL ADVISORY CTA */}
        <section className="py-10 bg-white rounded-xl mt-5 mb-8 border border-[#E5E7EB] shadow-sm">
          <div className="max-w-[1280px] mx-auto px-5 md:px-8">
            <div className="text-center mb-6">
              <h2 className="text-2xl md:text-3xl font-bold text-[#111827] mb-2">Ready to Take the Next Step?</h2>
              <p className="text-[#6B7280] max-w-2xl mx-auto text-sm md:text-base">
                Connect with our education advisors for personalized guidance on your academic journey
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              {/* Column 1: Expert Guidance */}
              <div className="bg-gradient-to-br from-[#F8FAFC] to-white border-2 border-[#E5E7EB] rounded-xl p-6 text-center hover:border-[#1E40AF] transition-all">
                <div className="w-16 h-16 bg-gradient-to-br from-[#1E40AF] to-[#1e40afdd] rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                </div>
                <h3 className="font-bold text-[#111827] mb-2">Expert Advisors</h3>
                <p className="text-sm text-[#6B7280] mb-4">Certified education counselors with 10+ years experience</p>
                <div className="flex items-center justify-center gap-1 text-[#F97316] text-sm font-semibold">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                  4.9/5 Rating
                </div>
              </div>
              
              {/* Column 2: Free Consultation */}
              <div className="bg-gradient-to-br from-[#F8FAFC] to-white border-2 border-[#E5E7EB] rounded-xl p-6 text-center hover:border-[#1E40AF] transition-all">
                <div className="w-16 h-16 bg-gradient-to-br from-[#F97316] to-[#fb7e2e] rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                  </svg>
                </div>
                <h3 className="font-bold text-[#111827] mb-2">Free Consultation</h3>
                <p className="text-sm text-[#6B7280] mb-4">Get personalized advice at no cost, no commitment required</p>
                <div className="flex items-center justify-center gap-1 text-[#059669] text-sm font-semibold">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  100% Free
                </div>
              </div>
              
              {/* Column 3: Fast Response */}
              <div className="bg-gradient-to-br from-[#F8FAFC] to-white border-2 border-[#E5E7EB] rounded-xl p-6 text-center hover:border-[#1E40AF] transition-all">
                <div className="w-16 h-16 bg-gradient-to-br from-[#059669] to-[#047857] rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <h3 className="font-bold text-[#111827] mb-2">Quick Response</h3>
                <p className="text-sm text-[#6B7280] mb-4">Get answers within 24 hours via chat, call, or email</p>
                <div className="flex items-center justify-center gap-1 text-[#1E40AF] text-sm font-semibold">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  24hr Response
                </div>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3 justify-center max-w-2xl mx-auto">
              <button
                onClick={handleConsultClick}
                className="px-6 py-3 bg-gradient-to-r from-[#F97316] to-[#fb7e2e] hover:shadow-xl text-white font-semibold rounded-xl transition-all flex items-center justify-center gap-2"
                data-testid="final-consult-button"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                Consult on WhatsApp
              </button>
              <button
                onClick={() => setShowAppointmentForm(true)}
                className="px-6 py-3 bg-white border-2 border-[#1E40AF] text-[#1E40AF] hover:bg-[#1E40AF] hover:text-white font-semibold rounded-xl transition-all flex items-center justify-center gap-2"
                data-testid="final-appointment-button"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                Book Appointment
              </button>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
      <WhatsAppCTA phone="60123456789" message={`Hi, I would like to inquire about ${program.program_name}`} />
      
      {showAppointmentForm && (
        <AppointmentForm
          onClose={() => setShowAppointmentForm(false)}
          onSubmit={handleAppointmentSubmit}
        />
      )}
    </div>
  );
};

export default ProgramDetail;
